/** @format */

import { Locale } from "../../../i18n/init";
import { ResourceType } from "../../../types";

/**
 * @returns The default description for the given resource type. Returns an empty string if the resource type doesn't have a default description
 */
export function getDefaultResourceDescription(
  locale: Locale,
  resourceType: ResourceType,
  title: string,
): string {
  if (locale === "en") {
    switch (resourceType) {
      case "BL":
        return (
          "<p>This blended learning resource contains interactive self-study content covering " +
          title +
          ". The resource is designed to complement traditional face-to-face teaching with an online learning pack that allows students to learn at their own pace.</p>" +
          "<p>It can be used as revision by students, as part of catch-up or for flipped learning.</p>" +
          "<p>It should not be seen as a way to deliver the content in a classroom setting and should always be blended with conventional methods.</p>"
        )
      case "KO":
        return (
          "A bank of sample knowledge organisers to support the learning of " +
          title +
          ". These could be used for revision or as a starting point for creating your own knowledge organisers."
        );
      case "SP":
        return "A downloadable planner to aid learners in tracking their understanding of the topics to be assessed. This will be an invaluable resource to aid revision by giving an instant overview of where gaps in knowledge need to be addressed.";
      case "CR":
        return "These front of class resources are designed to be utilised by the teacher to engender discussion, assess understanding or to model skills.";
      case "DT":
        return "This tool can be used to benchmark current knowledge and understanding and to assess progress after topics have been taught.";
      case "QB":
        return "Build your own papers to test your understanding of certain topics. The question bank also contains mark schemes so that you can mark your own work and check your progress.";
      case "OER":
        return "Check answers to past paper questions and consider the examiners comments. These resources will help you understand what the questions are asking for and help you avoid common pitfalls.";
      case "EWT":
        return "Aimed at learners, this Exam Walk Through resource offers practical hints and tips on how to effectively approach questions in the examination paper. Using questions from past exam papers, the PPT with audio help and audio script in the notes will walk you through a mock examination paper, helping you revise and practise useful exam techniques. You can complete the “mock” in a single sitting, tackle one or two questions at a time or revisit some parts of the presentation to reinforce your learning. Make sure you download the PPT to ensure the audio function works effectively, make sure your speakers / headphones are on and don’t forget to ask your teacher if there is anything you are unsure of.";
      case "OR":
        return "Valuable content that is designed to enhance other teaching and learning resources.";
      case "TP":
        return "Tools to aid teacher planning";
      default:
        return "";
    }
  } else if (locale === "cy") {
    switch (resourceType) {
      case "BL":
        return (
          "<p>Dyma adnodd dysgu cyfunol gyda chynnwys hunan-astudio rhyngweithiol sy'n ymdrin â " +
          title +
          ". Pwrpas yr adnodd yw cyfuno dysgu wyneb yn wyneb traddodiadol gyda phecyn dysgu ar-lein sy'n caniatáu i'r myfyrwyr ddysgu ar eu cyflymder eu hunain.</p>" +
          "<p>Gellir ei ddefnyddio fel adnodd adolygu ar gyfer y myfyrwyr, er mwyn dal i fyny neu ar gyfer gweithgareddau i ddysgu lle mae'r myfyrwyr yn darllen deunydd tu allan i'r dosbarth.</p>" +
          "<p>Ni ddylid dysgu'r cynnwys mewn ystafell ddosbarth, mae angen ei gyfuno gyda dulliau dysgu confensiynol bob amser.</p>"
        )
      case "KO":
        return (
          "Dyma gasgliad o enghreifftiau o drefnyddion gwybodaeth i gefnogi'r dysgu ar gyfer " +
          title +
          ". Gall y rhain gael eu defnyddio ar gyfer adolygu neu fel man cychwyn er mwyn creu eich trefnyddion gwybodaeth eich hunain."
        );
      case "SP":
        return "Cynlluniwr y gallwch ei lawrlwytho i gynorthwyo dysgwyr i ddilyn eu llwybr dysgu o'r pynciau sy'n cael eu hasesu. Bydd hyn yn adnodd hynod werthfawr i gynorthwyo adolygu trwy roi trosolwg sydyn o'r pynciau y mae angen i ddysgwyr ailymweld â nhw.";
      case "CR":
        return "Mae'r adnoddau blaen dosbarth hyn wedi'u cynllunio i'w defnyddio gan yr athro i ysgogi trafodaeth, asesu dealltwriaeth neu i fodelu sgiliau.";
      case "DT":
        return "Gellir defnyddio'r dull hwn i werthuso gwybodaeth a dealltwriaeth gyfredol ac i asesu cynnydd ar ôl i bynciau gael eu haddysgu.";
      case "QB":
        return "Adeiladwch eich papurau eich hun er mwyn profi eich dealltwriaeth o bynciau penodol. Mae’r banc cwestiynau hefyd yn cynnwys cynlluniau marcio fel eich bod yn gallu marcio eich gwaith eich hun a gweld eich cynnydd.";
      case "OER":
        return "Edrychwch ar atebion i gwestiynau hen bapurau ac ystyriwch sylwadau’r arholwr. Bydd yr adnoddau yma yn eich helpu chi i ddeall am beth mae’r arholwr yn chwilio yn eich atebion ac i’ch helpu i osgoi camgymeriadau cyffredin.";
      case "EWT":
        return "Wedi'i anelu at ddysgwyr, mae'r adnodd Arweiniad i’r Arholiad hwn yn cynnig awgrymiadau ymarferol ar sut i fynd i'r afael â chwestiynau yn y papur arholiad yn effeithiol. Gan ddefnyddio cwestiynau o chyn bapurau arholiad, bydd y PPT, gyda chymorth sain a sgript sain yn y nodiadau, yn eich tywys trwy ffug bapur arholiad, gan eich helpu i adolygu ac ymarfer technegau arholiad defnyddiol. Gallwch gwblhau'r “ffug arholiad” i gyd ar unwaith, mynd i’r afael ag un neu ddau o gwestiynau ar y tro neu ailedrych ar rai rhannau o'r cyflwyniad i atgyfnerthu'ch dysgu. Gwnewch yn siŵr eich bod yn llwytho’r PPT i lawr i sicrhau bod y sain yn gweithio'n effeithiol, gwnewch yn siŵr bod eich seinyddion / clustffonau ymlaen a pheidiwch ag anghofio gofyn i'ch athro os byddwch chi'n ansicr o unrhyw beth.";
      case "OR":
        return "Cynnwys gwerthfawr sydd wedi'i gynllunio i gyfoethogi adnoddau addysgu a dysgu eraill.";
      case "TP":
        return "Dulliau er mwyn helpu athrawon i gynllunio";
      default:
        return "";
    }
  }
  return "";
}
